// Images for the document generator: line thumbnails (0–3 per line) and the
// gallery (1–6 per document). Browser-side only — processing happens before
// upload so the stored file is already PDF-sized.
//
//   any image → EXIF-oriented (createImageBitmap imageOrientation:'from-image')
//             → long edge ≤ 1600 px → JPEG ~80 % on white
//
// JPEG, not WebP, on purpose: Chromium embeds a JPEG in the PDF as-is but
// re-encodes WebP losslessly, which is what would push a document past the
// 5 MB target.
//
// Storage: the PRIVATE doc-files bucket (migration 20260928093201), under
// img/<document_id>/. Each document owns its files — duplicating or revising
// copies them — so deleting one draft's image can never break another
// document. The preview reads them through short-lived signed URLs; the export
// worker downloads and embeds them as data: URIs.
//
// ⚠️ Images cross sides: a supplier's photo can land in a client quotation and
// a client's photo in a supplier RFQ. Nothing here screens pixels — the
// admin's review before approval is the control (owner, 2026-09-28; the same
// stance as security rule 6 for RFQ attachments).

import { sb } from '../../supabase';
import { newId } from './ids.js';

export const BUCKET = 'doc-files';
export const MAX_EDGE = 1600;
export const JPEG_QUALITY = 0.8;
export const MAX_LINE_IMAGES = 3;
export const MAX_GALLERY_IMAGES = 6;
const ACCEPT = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

const fail = (where, error) => {
  console.error(`[docgen/images] ${where}:`, error?.message || error);
  throw error;
};

export function isAcceptedImage(file) {
  return !!file && ACCEPT.includes(file.type);
}

// Fit (w, h) inside a MAX_EDGE box, never upscaling.
export function fitWithin(w, h, max = MAX_EDGE) {
  const s = Math.min(1, max / Math.max(w, h));
  return { width: Math.max(1, Math.round(w * s)), height: Math.max(1, Math.round(h * s)) };
}

/**
 * Decode, orient, optionally crop, resize and re-encode an image.
 * @param {Blob} blob
 * @param {{x:number,y:number,width:number,height:number}} [crop] in source pixels (after orientation)
 * @returns {Promise<{blob: Blob, width: number, height: number}>}
 */
export async function processImage(blob, crop = null) {
  let bmp;
  try {
    bmp = await createImageBitmap(blob, { imageOrientation: 'from-image' });
  } catch (e) {
    // HEIC from an iPhone is the usual case: Chrome cannot decode it.
    throw new Error('unsupported_image');
  }
  const src = crop
    ? { x: Math.max(0, Math.round(crop.x)), y: Math.max(0, Math.round(crop.y)), w: Math.round(crop.width), h: Math.round(crop.height) }
    : { x: 0, y: 0, w: bmp.width, h: bmp.height };
  const { width, height } = fitWithin(src.w, src.h);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#FFFFFF'; // transparency (PNG/GIF) becomes white, never black
  ctx.fillRect(0, 0, width, height);
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(bmp, src.x, src.y, src.w, src.h, 0, 0, width, height);
  bmp.close?.();
  const out = await new Promise((resolve, reject) => canvas.toBlob(
    (b) => (b ? resolve(b) : reject(new Error('encode_failed'))), 'image/jpeg', JPEG_QUALITY,
  ));
  return { blob: out, width, height };
}

const pathFor = (docId) => `img/${docId}/${newId()}.jpg`;

async function putObject(path, blob) {
  const { error } = await sb.storage.from(BUCKET).upload(path, blob, { contentType: 'image/jpeg', upsert: false });
  if (error) fail('upload', error);
}

/** Process and store one image, and record it against the document (and line). */
export async function addImage({ docId, lineId = null, file, position = 0, referenceOnly = false }) {
  const { blob, width, height } = await processImage(file);
  const path = pathFor(docId);
  await putObject(path, blob);
  const row = {
    id: newId(), document_id: docId, line_id: lineId, position, storage_path: path,
    width, height, bytes: blob.size, reference_only: referenceOnly,
  };
  const { data, error } = await sb.from('doc_images').insert(row).select('*').single();
  if (error) {
    await sb.storage.from(BUCKET).remove([path]); // do not leave an orphan
    fail('addImage(row)', error);
  }
  return data;
}

/** Replace an image's file with a cropped version of itself. */
export async function cropImage(image, crop) {
  const { data: file, error } = await sb.storage.from(BUCKET).download(image.storage_path);
  if (error) fail('cropImage(download)', error);
  const { blob, width, height } = await processImage(file, crop);
  const path = pathFor(image.document_id);
  await putObject(path, blob);
  const { data, error: uErr } = await sb.from('doc_images')
    .update({ storage_path: path, width, height, bytes: blob.size }).eq('id', image.id).select('*').single();
  if (uErr) {
    await sb.storage.from(BUCKET).remove([path]);
    fail('cropImage(row)', uErr);
  }
  await sb.storage.from(BUCKET).remove([image.storage_path]);
  return data;
}

export async function updateImage(id, patch) {
  const allowed = {};
  for (const k of ['caption_ar', 'caption_en', 'reference_only', 'position']) if (k in patch) allowed[k] = patch[k];
  const { data, error } = await sb.from('doc_images').update(allowed).eq('id', id).select('*').single();
  if (error) fail('updateImage', error);
  return data;
}

export async function removeImages(images) {
  if (!images.length) return;
  const { error } = await sb.from('doc_images').delete().in('id', images.map((i) => i.id));
  if (error) fail('removeImages(rows)', error);
  const { error: sErr } = await sb.storage.from(BUCKET).remove(images.map((i) => i.storage_path));
  if (sErr) console.error('[docgen/images] removeImages(objects):', sErr.message); // rows are gone; an orphan file is harmless
}

/** Signed URLs for the preview, keyed by storage path. */
export async function signedUrls(paths, expiresIn = 3600) {
  const unique = [...new Set(paths)].filter(Boolean);
  if (!unique.length) return {};
  const { data, error } = await sb.storage.from(BUCKET).createSignedUrls(unique, expiresIn);
  if (error) fail('signedUrls', error);
  const out = {};
  for (const r of data || []) if (r.signedUrl) out[r.path] = r.signedUrl;
  return out;
}

/**
 * Copy images into another document (duplicate / revise), remapping line ids.
 * `lineMap` maps old line id → new line id; gallery images keep line_id null.
 */
export async function copyImagesTo(images, targetDocId, lineMap) {
  const rows = [];
  for (const im of images) {
    const newLine = im.line_id ? lineMap.get(im.line_id) : null;
    if (im.line_id && !newLine) continue;
    const path = pathFor(targetDocId);
    const { error } = await sb.storage.from(BUCKET).copy(im.storage_path, path);
    if (error) fail('copyImagesTo(copy)', error);
    rows.push({
      id: newId(), document_id: targetDocId, line_id: newLine, position: im.position, storage_path: path,
      width: im.width, height: im.height, bytes: im.bytes, caption_ar: im.caption_ar, caption_en: im.caption_en,
      reference_only: im.reference_only,
    });
  }
  if (rows.length) {
    const { error } = await sb.from('doc_images').insert(rows);
    if (error) fail('copyImagesTo(rows)', error);
  }
}

/** Every file under a draft's folder — removed when the draft is deleted. */
export async function removeDocumentFolder(docId) {
  const { data, error } = await sb.storage.from(BUCKET).list(`img/${docId}`, { limit: 1000 });
  if (error) { console.error('[docgen/images] list:', error.message); return; }
  const paths = (data || []).map((f) => `img/${docId}/${f.name}`);
  if (paths.length) {
    const { error: rErr } = await sb.storage.from(BUCKET).remove(paths);
    if (rErr) console.error('[docgen/images] removeDocumentFolder:', rErr.message);
  }
}

// ── picking from elsewhere ─────────────────────────────────────────────
/** Images already used in other documents, newest first. */
export async function recentDocumentImages({ excludeDocId, limit = 60 } = {}) {
  const { data, error } = await sb.from('doc_images')
    .select('id, document_id, storage_path, width, height, caption_ar, caption_en, created_at, doc_documents(ref_no, kind, content)')
    .neq('document_id', excludeDocId || '00000000-0000-0000-0000-000000000000')
    .order('created_at', { ascending: false }).limit(limit);
  if (error) fail('recentDocumentImages', error);
  return data || [];
}

/** Copy one image from another document into this one. */
export async function importFromDocument({ source, docId, lineId = null, position = 0 }) {
  const path = pathFor(docId);
  const { error } = await sb.storage.from(BUCKET).copy(source.storage_path, path);
  if (error) fail('importFromDocument(copy)', error);
  const { data, error: iErr } = await sb.from('doc_images').insert({
    id: newId(), document_id: docId, line_id: lineId, position, storage_path: path,
    width: source.width, height: source.height, caption_ar: source.caption_ar, caption_en: source.caption_en,
  }).select('*').single();
  if (iErr) { await sb.storage.from(BUCKET).remove([path]); fail('importFromDocument(row)', iErr); }
  return data;
}

/** The linked request's image attachments (private request-attachments bucket, admin read). */
export async function requestImageAttachments(requestId) {
  if (!requestId) return [];
  const { data, error } = await sb.from('requests').select('attachment_paths').eq('id', requestId).maybeSingle();
  if (error) fail('requestImageAttachments', error);
  const paths = (data?.attachment_paths || []).filter((p) => /\.(jpe?g|png|webp|gif)$/i.test(p));
  if (!paths.length) return [];
  const { data: signed, error: sErr } = await sb.storage.from('request-attachments').createSignedUrls(paths, 3600);
  if (sErr) fail('requestImageAttachments(sign)', sErr);
  return (signed || []).filter((s) => s.signedUrl).map((s) => ({ path: s.path, url: s.signedUrl }));
}

/** Download a request attachment and add it as a (processed) document image. */
export async function importFromRequest({ path, docId, lineId = null, position = 0 }) {
  const { data: file, error } = await sb.storage.from('request-attachments').download(path);
  if (error) fail('importFromRequest(download)', error);
  return addImage({ docId, lineId, file, position });
}
